import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ReportHeader, SummaryCards, FilterPanel, ExportButtons } from '../../components/reports';
import { D3LineChart, D3AreaChart } from '../../components/charts';
import { getAmendmentTrends } from '../../services/project750kService';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';

const PageContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const ChartSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
    font-family: 'Segoe UI', sans-serif;
  }

  th {
    background-color: #0077B5;
    color: white;
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }
`;

const AmendmentIdsCell = styled.div`
  font-family: 'Segoe UI', monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-line;
  max-width: 400px;
  word-break: break-word;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 18px;
  color: #0077B5;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoBanner = styled.div`
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
`;

const AmendmentTrends = () => {
  const [trends, setTrends] = useState([]);
  const [recentAmendments, setRecentAmendments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ year: '', county: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (appliedFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const result = await getAmendmentTrends(appliedFilters);
      setTrends(result.trends || []);
      setRecentAmendments(result.recentAmendments || []);
      setSummary(result.summary || {});
      setMeta(result.meta || null);
    } catch (err) {
      setError(`Error loading amendment trends: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setTimeout(() => loadData(newFilters), 500);
  };

  const handleExportExcel = useCallback(async () => {
    try {
      await exportToExcel(recentAmendments, 'Amendment_Trends', 'Amendments', filters);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  }, [recentAmendments, filters]);

  const handleExportPDF = useCallback(async () => {
    try {
      await exportToPDF({
        title: 'Amendment Trends Report',
        data: recentAmendments,
        chartElementId: 'line-chart',
        summary: {
          'Total Amendments': summary?.totalAmendments || 0,
          'Avg per Month': summary?.avgPerMonth || 0,
          'Months Tracked': summary?.monthsTracked || 0
        },
        filename: 'Amendment_Trends',
        filters
      });
    } catch (err) {
      setError(`PDF export failed: ${err.message}`);
    }
  }, [recentAmendments, summary, filters]);

  const filterConfig = [
    {
      type: 'text',
      label: 'Year',
      value: filters.year,
      onChange: (value) => handleFilterChange('year', value),
      placeholder: 'e.g., 2025'
    },
    {
      type: 'text',
      label: 'County',
      value: filters.county,
      onChange: (value) => handleFilterChange('county', value),
      placeholder: 'e.g., YUMA'
    }
  ];

  const summaryCards = summary ? [
    { label: 'Total Amendments', value: summary.totalAmendments },
    { label: 'Avg per Month', value: summary.avgPerMonth },
    { label: 'Months Tracked', value: summary.monthsTracked }
  ] : [];

  // Prepare line chart data (monthly counts - non-cumulative)
  const lineChartData = trends.map(t => ({
    x: t.month,
    y: t.count
  }));

  // Prepare cumulative data (running total)
  let cumulativeTotal = 0;
  const cumulativeChartData = trends.map(t => {
    cumulativeTotal += t.count;
    return {
      x: t.month,
      y: cumulativeTotal
    };
  });

  if (loading) return <LoadingSpinner>Loading amendment trends...</LoadingSpinner>;

  return (
    <PageContainer>
      <ReportHeader icon="📊" title="Amendment Trends" />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {meta && (
        <InfoBanner>
          ℹ️ Trends based on <strong>{meta.recordsScanned?.toLocaleString() || 0} records analyzed</strong>
          {meta.hasMore && ' (sample data)'}
        </InfoBanner>
      )}

      <FilterPanel filters={filterConfig} />
      
      <SummaryCards cards={summaryCards} />
      
      <ExportButtons
        onExportExcel={handleExportExcel}
        onExportPDF={handleExportPDF}
        loading={loading}
      />

      <ChartSection id="line-chart">
        <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Amendments Over Time</h3>
        <D3LineChart
          data={lineChartData}
          xKey="x"
          yKey="y"
          xLabel="Month"
          yLabel="Amendment Count"
          height="400px"
          showDots={false}
        />
      </ChartSection>

      <ChartSection id="area-chart">
        <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Cumulative Trends</h3>
        <D3AreaChart
          data={cumulativeChartData}
          xKey="x"
          yKeys={['y']}
          xLabel="Month"
          yLabel="Cumulative Amendment Count"
          labels={['Total Amendments']}
          height="400px"
        />
      </ChartSection>

      <TableContainer>
        <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>
          Recent Amendments (Top 50)
        </h3>
        <Table>
          <thead>
            <tr>
              <th>Debtor Name</th>
              <th>Record ID</th>
              <th>Amendment Count</th>
              <th>Amendment IDs</th>
            </tr>
          </thead>
          <tbody>
            {recentAmendments.map((amendment, index) => {
              // Parse and sort amendments by date (newest first)
              const parseAmendmentDate = (amendmentStr) => {
                // Match pattern like " - 10/26/2016" or " - 12/30/2024"
                const dateMatch = amendmentStr.match(/\s+-\s+(\d{2})\/(\d{2})\/(\d{4})/);
                if (dateMatch) {
                  const month = parseInt(dateMatch[1], 10) - 1; // Month is 0-indexed
                  const day = parseInt(dateMatch[2], 10);
                  const year = parseInt(dateMatch[3], 10);
                  return new Date(year, month, day);
                }
                return null;
              };

              // Split amendments, parse dates, sort by date (newest first), then format
              const amendmentArray = (amendment.amendments || '')
                .split(/[,;]/)
                .map(a => a.trim())
                .filter(a => a.length > 0)
                .map(amendmentStr => ({
                  text: amendmentStr,
                  date: parseAmendmentDate(amendmentStr)
                }))
                .sort((a, b) => {
                  // Sort by date (newest first)
                  // If no date, put at the end
                  if (!a.date && !b.date) return 0;
                  if (!a.date) return 1;
                  if (!b.date) return -1;
                  return b.date - a.date; // Descending (newest first)
                })
                .map(item => item.text);
              
              const formattedAmendments = amendmentArray.join('\n');
              
              return (
                <tr key={index}>
                  <td><strong>{amendment.debtorName}</strong></td>
                  <td>{amendment.recordId}</td>
                  <td>{amendment.amendmentCount}</td>
                  <td>
                    <AmendmentIdsCell>{formattedAmendments}</AmendmentIdsCell>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default AmendmentTrends;

